'use client';

import { useEffect, useRef, useState } from 'react';
import Link from 'next/link';
import { format, parseISO } from 'date-fns';
import { getInternationalTime } from '@utils/native-dates';
import { CopyLink } from '@v2/containers/EventsSelectionList/EventsSelectionList';
import { TeamCalendarGame } from '@v2/services/team-schedule';
import { Button } from '@v2/ui/Button/Button';
import { ChevronLeft, ChevronRight } from '@v2/ui/Icon/Arrow';
import { cn } from '@v2/utils/cn';
import {
  buildTeamCalendarMonths,
  getTeamGameHref,
  getTeamGameEventLink,
  TeamCalendarRange,
} from '../util/teamSchedule';

interface Props {
  games: TeamCalendarGame[];
  onSelect: (game: TeamCalendarGame) => void;
}

// Supplier-flagged TBD games carry their scheduled date with a placeholder time.
function TeamGameTime({ game }: { game: TeamCalendarGame }) {
  return game.isTbd ? (
    <span>Time TBD</span>
  ) : (
    <time dateTime={game.occursAtLocal}>
      {getInternationalTime(game.occursAtLocal, true)}
    </time>
  );
}

export function TeamScheduleList({ games, onSelect }: Props) {
  return (
    <ul className="space-y-4">
      {games.map((game) => (
        <li key={game.eventId} className="flex items-center gap-2">
          <Link
            href={getTeamGameHref(game)}
            prefetch={false}
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => onSelect(game)}
            className="grid min-h-20 min-w-0 flex-1 grid-cols-[64px_minmax(0,1fr)_auto] items-center gap-3 md:gap-4"
          >
            <time
              dateTime={game.date}
              className="flex h-20 flex-col overflow-hidden rounded-xl border border-gray-200 text-center text-gray-800"
            >
              <span className="flex flex-1 flex-col items-center justify-center">
                <span className="text-xs font-semibold uppercase">
                  {format(parseISO(game.date), 'MMM')}
                </span>
                <span className="text-2xl font-bold leading-6">
                  {format(parseISO(game.date), 'dd')}
                </span>
              </span>
              <span className="bg-gray-100 py-1 text-xs font-semibold uppercase text-gray-600">
                {format(parseISO(game.date), 'EEE')}
              </span>
            </time>
            <div className="min-w-0">
              <h3
                aria-label={game.name}
                className="break-words text-sm font-semibold leading-5 text-gray-800 md:line-clamp-2 md:text-base md:leading-6"
              >
                <span className="md:hidden">
                  {game.isHome ? 'vs' : 'at'} {game.opponent.name}
                </span>
                <span className="hidden md:inline">{game.name}</span>
              </h3>
              <p className="text-xs leading-5 text-gray-600 md:mt-1 md:text-sm">
                <span className="sr-only">
                  {game.isHome ? 'Home' : 'Away'}.{' '}
                </span>
                <TeamGameTime game={game} />
                <span className="hidden md:inline"> · </span>
                <span className="block md:inline">
                  {[game.venue.name, game.venue.location]
                    .filter(Boolean)
                    .join(', ')}
                </span>
              </p>
            </div>
            <span className="inline-flex min-h-10 max-w-24 items-center justify-center rounded-lg bg-primary-500 px-2 text-center text-xs font-semibold text-white md:max-w-none md:px-4 md:text-sm">
              View Packages
            </span>
          </Link>
          <CopyLink
            eventId={game.eventId}
            eventLink={getTeamGameEventLink(game)}
          />
        </li>
      ))}
    </ul>
  );
}

function TeamCalendarGameCard({
  game,
  onSelect,
}: Pick<Props, 'onSelect'> & { game: TeamCalendarGame }) {
  return (
    <Link
      href={getTeamGameHref(game)}
      prefetch={false}
      target="_blank"
      rel="noopener noreferrer"
      aria-label={`${game.name}, ${format(parseISO(game.date), 'MMMM d, yyyy')}, ${game.isTbd ? 'time TBD' : getInternationalTime(game.occursAtLocal, true)}`}
      onClick={() => onSelect(game)}
      className="flex h-full flex-col overflow-hidden rounded-lg border border-gray-200 focus-visible:outline focus-visible:outline-2 focus-visible:outline-primary-500 md:min-h-36"
    >
      <span
        className={cn(
          'flex items-start justify-between gap-2 px-3 py-2 text-sm md:px-2 md:py-1.5 md:text-xs',
          game.isHome
            ? 'bg-primary-500 text-white'
            : 'bg-gray-100 text-gray-600'
        )}
      >
        <time dateTime={game.date}>
          <span className="md:hidden">
            {format(parseISO(game.date), 'EEE, MMM d')}
          </span>
          <span className="hidden md:inline">
            {format(parseISO(game.date), 'd')}
          </span>
        </time>
        <TeamGameTime game={game} />
      </span>
      <span className="flex-1 bg-white px-4 py-4 text-sm md:px-2 md:py-3 md:text-center md:text-xs">
        <span className="block break-words font-semibold text-gray-800">
          {game.isHome ? 'vs' : 'at'} {game.opponent.name}
        </span>
        <span className="mt-1 block text-xs text-gray-500">
          {game.isHome ? 'Home' : 'Away'}
          <span className="md:hidden">
            {' '}
            ·{' '}
            {[game.venue.name, game.venue.location].filter(Boolean).join(', ')}
          </span>
        </span>
      </span>
      <span className="block border-t border-gray-200 bg-white px-4 py-3 text-center text-sm font-medium text-primary-500 md:border-0 md:px-1 md:pb-2 md:pt-0 md:text-xs">
        View Packages
      </span>
    </Link>
  );
}

export function TeamScheduleCalendar({
  games,
  onSelect,
  range,
}: Props & { range: TeamCalendarRange }) {
  const months = buildTeamCalendarMonths(range, games);
  const isInRange = (date: string) =>
    date >= range.startDate && date <= range.endDate;
  const monthDays = (month: (typeof months)[number]) =>
    month.weeks
      .flatMap((week) => week)
      .filter(
        (day): day is NonNullable<typeof day> =>
          day !== null && isInRange(day.date)
      );
  const firstGameDate =
    games.find((game) => isInRange(game.date))?.date ?? range.startDate;
  const [selectedDate, setSelectedDate] = useState(firstGameDate);
  const loadedThroughRef = useRef(range.endDate);
  const selectedMonthRef = useRef<HTMLButtonElement>(null);
  const selectedDayRef = useRef<HTMLButtonElement>(null);
  const currentDate = isInRange(selectedDate) ? selectedDate : firstGameDate;
  const activeMonth =
    months.find((month) => month.month === currentDate.slice(0, 7)) ??
    months[0];
  const activeDays = monthDays(activeMonth);
  const agendaDays = activeDays.filter((day) => day.games.length > 0);
  const activeMonthIndex = months.indexOf(activeMonth);
  const selectMonth = (month: (typeof months)[number]) => {
    const days = monthDays(month);
    setSelectedDate(days.find((day) => day.games.length)?.date ?? days[0].date);
  };

  useEffect(() => {
    const previousEndDate = loadedThroughRef.current;
    loadedThroughRef.current = range.endDate;
    if (range.endDate > previousEndDate) {
      setSelectedDate(
        games.find(
          (game) => game.date > previousEndDate && game.date <= range.endDate
        )?.date ?? `${range.endDate.slice(0, 7)}-01`
      );
    }
  }, [games, range.endDate]);

  useEffect(() => {
    for (const button of [selectedMonthRef.current, selectedDayRef.current]) {
      if (button?.parentElement) {
        button.parentElement.scrollLeft =
          button.offsetLeft -
          (button.parentElement.clientWidth - button.offsetWidth) / 2;
      }
    }
  }, [currentDate]);

  return (
    <div className="space-y-6">
      <div className="hidden md:block">
        <div className="mb-3 flex items-center justify-between gap-3">
          <p
            aria-live="polite"
            className="text-base font-semibold text-gray-800"
          >
            {activeMonth.label}
          </p>
          <div
            role="group"
            aria-label="Calendar month navigation"
            className="flex gap-2"
          >
            <Button
              type="button"
              variant="secondary"
              size="sm"
              className="h-11 w-11"
              aria-label="Previous month"
              disabled={activeMonthIndex === 0}
              onClick={() => selectMonth(months[activeMonthIndex - 1])}
            >
              <ChevronLeft size="20" aria-hidden="true" />
            </Button>
            <Button
              type="button"
              variant="secondary"
              size="sm"
              className="h-11 w-11"
              aria-label="Next month"
              disabled={activeMonthIndex === months.length - 1}
              onClick={() => selectMonth(months[activeMonthIndex + 1])}
            >
              <ChevronRight
                size="20"
                stroke="currentColor"
                aria-hidden="true"
              />
            </Button>
          </div>
        </div>
        <div
          aria-hidden="true"
          className="mb-3 grid grid-cols-7 border-y border-gray-200 py-3 text-center text-xs text-gray-500"
        >
          {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((day) => (
            <span key={day}>{day}</span>
          ))}
        </div>
        <div className="min-w-0">
          <table className="w-full table-fixed border-separate border-spacing-1 text-left text-sm lg:border-spacing-2">
            <caption className="sr-only">{activeMonth.label}</caption>
            <thead className="sr-only">
              <tr>
                {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(
                  (day) => (
                    <th key={day} scope="col">
                      {day}
                    </th>
                  )
                )}
              </tr>
            </thead>
            <tbody>
              {activeMonth.weeks
                .filter((week) =>
                  week.some((day) => day && isInRange(day.date))
                )
                .map((week, weekIndex) => (
                  <tr key={weekIndex}>
                    {week.map((cell, dayIndex) => (
                      <td
                        key={cell?.date ?? dayIndex}
                        className="h-36 p-0 align-top"
                      >
                        {cell && isInRange(cell.date) ? (
                          cell.games.length ? (
                            <div className="flex min-h-36 flex-col gap-1">
                              {cell.games.map((game) => (
                                <TeamCalendarGameCard
                                  key={game.eventId}
                                  game={game}
                                  onSelect={onSelect}
                                />
                              ))}
                            </div>
                          ) : (
                            <time
                              dateTime={cell.date}
                              className="block h-full min-h-36 rounded-lg border border-gray-200 bg-white px-2 py-1.5 text-xs text-gray-500"
                            >
                              {cell.day}
                            </time>
                          )
                        ) : (
                          <div className="h-full min-h-36 rounded-lg bg-gray-50" />
                        )}
                      </td>
                    ))}
                  </tr>
                ))}
            </tbody>
          </table>
        </div>
      </div>
      <section aria-label="Monthly game agenda" className="min-w-0 md:hidden">
        <div
          role="group"
          aria-label="Calendar months"
          className="no-scrollbar relative mb-4 flex gap-2 overflow-x-auto pb-1"
        >
          {months.map((month) => (
            <button
              key={month.month}
              ref={
                activeMonth.month === month.month ? selectedMonthRef : undefined
              }
              type="button"
              aria-pressed={activeMonth.month === month.month}
              onClick={() => selectMonth(month)}
              className={cn(
                'min-h-11 shrink-0 rounded-full px-4 text-sm font-semibold focus-visible:outline focus-visible:outline-2 focus-visible:outline-primary-500',
                activeMonth.month === month.month
                  ? 'bg-gray-800 text-white'
                  : 'text-gray-500'
              )}
            >
              {month.label}
            </button>
          ))}
        </div>
        <div
          role="group"
          aria-label={`${activeMonth.label} game dates`}
          className="no-scrollbar relative mb-5 flex gap-2 overflow-x-auto pb-1"
        >
          {activeDays.map((day) => (
            <button
              key={day.date}
              ref={day.date === currentDate ? selectedDayRef : undefined}
              type="button"
              aria-label={format(parseISO(day.date), 'EEEE, MMMM d, yyyy')}
              aria-pressed={day.date === currentDate}
              disabled={!day.games.length}
              onClick={() => {
                setSelectedDate(day.date);
                document
                  .getElementById(`team-calendar-day-${day.date}`)
                  ?.scrollIntoView({
                    block: 'nearest',
                    behavior: window.matchMedia(
                      '(prefers-reduced-motion: reduce)'
                    ).matches
                      ? 'auto'
                      : 'smooth',
                  });
              }}
              className={cn(
                'flex min-h-16 w-11 shrink-0 flex-col items-center justify-center gap-1 rounded-lg text-sm focus-visible:outline focus-visible:outline-2 focus-visible:outline-primary-500 disabled:cursor-default',
                day.date === currentDate
                  ? 'bg-primary-500 text-white'
                  : 'text-gray-600 disabled:text-gray-400'
              )}
            >
              <span>{format(parseISO(day.date), 'EEE')}</span>
              <span className="font-semibold">
                {format(parseISO(day.date), 'dd')}
              </span>
            </button>
          ))}
        </div>
        {agendaDays.length ? (
          <ul className="space-y-4">
            {agendaDays.map((day) => (
              <li
                key={day.date}
                id={`team-calendar-day-${day.date}`}
                className="scroll-mt-4 space-y-4"
              >
                {day.games.map((game) => (
                  <TeamCalendarGameCard
                    key={game.eventId}
                    game={game}
                    onSelect={onSelect}
                  />
                ))}
              </li>
            ))}
          </ul>
        ) : (
          <p
            role="status"
            className="rounded-lg border border-gray-200 bg-gray-50 p-6 text-sm text-gray-600"
          >
            No games scheduled in {activeMonth.label}.
          </p>
        )}
      </section>
    </div>
  );
}
